import { UPLOAD_STATUS } from "@/constants/uploadStatus";
import useAppStore from "@/context/store";
import { uploadSimilarFile } from "@/services";
import compressImg from "@/utils/compressImg";
import getImageBySrc from "@/utils/getImageBySrc";
import getReaderByFile from "@/utils/getReaderByFile";
import { useEventListener } from "ahooks";
import { Button, Upload } from "antd";
import classNames from "classnames";
import { useCallback, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import HBMessage from "../HBMessage";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";

const { Dragger } = Upload;

export default function UploadDragger({
  onDone,
  onStatus,
  acceptMimeTypes,
  maxWidth,
  maxHeight,
  tip,
}) {
  const user = useAppStore(({ user }) => user);
  const [img, setImg] = useState(null);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState(false);
  const fileRef = useRef(null);
  const containerRef = useRef(null);
  const { login } = useLogin();

  const onUpload = useCallback(
    async (file) => {
      if (!user) {
        login();
        return;
      }

      setErr(false);
      setProgress(0);
      fileRef.current = file;

      let newFile;
      let newSrc;
      // 压缩图片
      const result = await compressImg(file, {
        quality: 0.7,
        maxWidth,
        maxHeight,
      });

      if (!result) {
        // 压缩图片失败，按照校验流程处理
        newFile = file;
        const reader = await getReaderByFile(newFile);
        if (!reader) {
          HBMessage.error("图片处理失败");
          return Promise.reject();
        }
        newSrc = reader.result;
        const img = await getImageBySrc(newSrc);
        if (!img) {
          HBMessage.error("获取图片失败");
          return Promise.reject();
        }
      } else {
        // 压缩图片成功
        newFile = result.file;
        newSrc = result.src;
      }

      setImg(newSrc);

      try {
        onStatus && onStatus(UPLOAD_STATUS.UPLOADING);
        const result = await uploadSimilarFile(newFile, ({ loaded, total }) => {
          const progress = Math.trunc((loaded / total) * 100);
          setProgress(progress);
        });
        if (!result.err) {
          onDone && onDone(result);
          setImg(null);
          setProgress(0);
          onStatus && onStatus(UPLOAD_STATUS.UPLOADED);
        } else {
          setErr(true);
          onStatus && onStatus(UPLOAD_STATUS.ERROR);
        }
      } catch (err) {
        setErr(true);
        onStatus && onStatus(UPLOAD_STATUS.ERROR);
      }
    },
    [login, maxHeight, maxWidth, onDone, onStatus, user]
  );

  const onClick = useCallback(() => {
    if (!user) {
      login();
      return;
    }
    containerRef.current?.querySelector("input[type=file]")?.click();
  }, [login, user]);

  useEventListener("paste", (event) => {
    const items = event.clipboardData && event.clipboardData.items;
    let file = null;
    if (items && items.length) {
      // 检索剪切板items
      for (var i = 0; i < items.length; i++) {
        if (acceptMimeTypes.some((type) => type === items[i].type)) {
          file = items[i].getAsFile();
          break;
        }
      }
    }
    if (file) {
      onUpload(file);
    }
  });

  const copyText = useMemo(() => {
    return typeof window !== "undefined" && window.GLOBAL_INFO_OS === "macos"
      ? "Command + V"
      : "Ctrl + V";
  }, []);

  return (
    <div
      className={classNames([styles.container, err ? styles.err : null])}
      data-gd-click="button_click"
      data-button-name="以图搜图弹窗"
      data-resource-title="以图搜图弹窗"
      data-gd-expose="resource_loc_expose"
      data-resource-type="弹窗"
      ref={containerRef}
    >
      <Dragger
        className={styles.uploader}
        beforeUpload={onUpload}
        accept={acceptMimeTypes.join(",")}
        openFileDialogOnClick={false}
        fileList={null}
        customRequest={() => {}}
      >
        <div className={styles.wrapper}>
          <If condition={img}>
            <Then>
              <div className={styles.uploading}>
                <div className={styles.img}>
                  <div
                    className={styles.cover}
                    style={{ width: `${progress}%` }}
                  ></div>
                  <div className={styles.mask}></div>
                  <img src={img} alt="图片" />
                </div>
                <div className={styles.progress}>{progress}%</div>
              </div>
            </Then>
            <Else>
              <div className={styles.pre}>
                <Button
                  type="primary"
                  className={styles.redButton}
                  onClick={onClick}
                >
                  上传图片
                </Button>
                <div className={styles.title}>
                  或拖拽图片 ，或截图后<span>{copyText}</span>
                  上传图片
                </div>
                <When condition={!!tip}>
                  <div className={styles.subtitle}>{tip}</div>
                </When>
              </div>
            </Else>
          </If>
        </div>
      </Dragger>
      <Button className={styles.button} onClick={onClick}>
        选择图片
      </Button>
    </div>
  );
}
